import cloneDeep from 'lodash.clonedeep';
const {
  GRID_SIZE,
  SCORE_PLACEMENT,
  SCORE_FULL,
  SCORE_EMPTY_GRID
} = require('@/helpers/board-constants.js');
const { to1d } = require('@/helpers/utils.js');
import { TILINGS_LIST } from '@/helpers/tilings.js';
const {
  inBounds,
  checkFullRowsAndColumns,
  checkHasSpace
} = require('@/helpers/board.js');
import {
  range,
  sample,
  shuffle
} from '@/helpers/utils/array.js';

function drawStockUtil() {
  const stock = range(4).map(() => sample(TILINGS_LIST)).flat();
  shuffle(stock);
  return stock;
}

function drawDeckUtil(room) {
  const deckSize = 3;
  if (room.stock.length < deckSize) {
    room.stock = drawStockUtil();
  }
  const deck = range(deckSize).map((i) => ({
    ref: room.stock[i], x: 0, y: 0,
  }));
  room.deck = deck;
  room.stock = room.stock.slice(deckSize);
}

export function createRoom() {
  const data = {
    board: new Array(100).fill(null),
    stock: drawStockUtil(),
    lastMove: null,
    currentMove: null,
    status: "playing",
    score: 0
  };
  drawDeckUtil(data);
  return data;
}

export function loadRoom() {
  const room = localStorage.getItem("pebbles-room");
  if (room) return JSON.parse(atob(room));
  return null;
}

export function saveRoom(room) {
  localStorage.setItem("pebbles-room", btoa(JSON.stringify(room)));
}

export function clearRoom() {
  localStorage.removeItem("pebbles-room");
}

export function drawStock(room) {
  room = cloneDeep(room);
  const stock = drawStockUtil();
  room.stock = stock;
  return room;
}

export function drawDeck(room) {
  room = cloneDeep(room);
  drawDeckUtil(room);
  return room;
}

export function placePiece({ room, data }) {
  const { ref, index, x, y, cells } = data;
  room = cloneDeep(room);
  const { board, deck, score } = room;
  room.lastMove = {
    board: cloneDeep(board),
    deck: cloneDeep(deck),
    score,
  };
  room.currentMove = null;

  const newBoard = cloneDeep(board);
  for (let cell of cells) {
    if (inBounds(x + cell.x, y + cell.y)) {
      newBoard[to1d(x + cell.x, y + cell.y)] = ref;
    }
  }
  deck.splice(index, 1, null);
  let remainingDeck = deck.filter((p) => p !== null);
  if (remainingDeck.length === 0) {
    drawDeckUtil(room);
    remainingDeck = room.deck.filter((p) => p !== null);
  } else {
    room.deck = deck;
  }
  const full = checkFullRowsAndColumns(newBoard);
  const nFull = full.rows.length + full.cols.length;
  if (nFull > 0) {
    room.score += SCORE_FULL * nFull * nFull;
    full.rows.forEach((row) => {
      range(GRID_SIZE).forEach((i) => {
        newBoard[to1d(i, row)] = null;
      });
    });
    full.cols.forEach((col) => {
      range(GRID_SIZE).forEach((i) => {
        newBoard[to1d(col, i)] = null;
      });
    });
  }
  room.score += SCORE_PLACEMENT;
  room.board = newBoard;

  // check for game over
  const emptyCells = newBoard
    .map((c, i) => c === null ? i : null)
    .filter((i) => i !== null);
  if (emptyCells.length === newBoard.length) {
    room.score += SCORE_EMPTY_GRID;
  } else {
    if (!checkHasSpace(newBoard, emptyCells, remainingDeck)) {
      const bestScore = localStorage.getItem("pebbles-best-score");
      if (!bestScore || parseInt(bestScore) < room.score) {
        localStorage.setItem("pebbles-best-score", room.score);
      }
      room.status = "finished";
    }
  }

  return room;
}

export function undo(room) {
  if (!room.lastMove) return null;
  room = cloneDeep(room);
  room.currentMove = {
    board: room.board,
    deck: room.deck,
    score: room.score,
  };
  const { board, deck, score } = room.lastMove;
  room.board = board;
  room.deck = deck;
  room.score = score;
  room.lastMove = null;
  return room;
}

export function redo(room) {
  if (!room.currentMove) return null;
  room = cloneDeep(room);
  room.lastMove = {
    board: room.board,
    deck: room.deck,
    score: room.score,
  };
  const { board, deck, score } = room.currentMove;
  room.board = board;
  room.deck = deck;
  room.score = score;
  room.currentMove = null;
  return room;
}
